import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { formatDate, formatNumero, generateAddress } from "../../utils/Utilitats";

const PAGE_SIZES = [10, 25, 50];

function SeleccionarPressupost({
  onPressupostSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  onOfertes,
  onVersions,
  selectedPressupost,
  idClient,
  setPressupostos,
  reloadToken = 0,
  filters,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");
  const pucVersions = tincPermis(rolUsuari, "ACCES");
  const teAccionsFila = !!(onConsultar || onModificar || onEsborrar || onOfertes || onVersions);

  const idClientInicial = idClient?.idClients || (typeof idClient === "number" ? idClient : null);

  const [localText, setLocalText] = useState("");
  const effFilterText = filters?.filterText !== undefined ? filters.filterText : localText;
  const setEffFilterText = filters?.setFilterText ?? setLocalText;

  const [localEstat, setLocalEstat] = useState(idClientInicial ? "" : "Esborrany");
  const effEstat = filters?.estatFilters !== undefined ? filters.estatFilters : localEstat;
  const setEffEstat = filters?.setEstatFilters ?? setLocalEstat;

  const [localClient, setLocalClient] = useState(idClientInicial || null);
  const effClient = filters?.clientFilter !== undefined ? filters.clientFilter : localClient;
  const setEffClient = filters?.setClientFilter ?? setLocalClient;

  const didInitRef = useRef(false);
  const prevIdClientRef = useRef(idClient);

  useEffect(() => {
    const isEmptyText = !effFilterText || effFilterText === "";
    const isEmptyEstat = effEstat === "" || effEstat == null;
    const isEmptyClient = effClient === "" || effClient == null;
    const firstTime = !didInitRef.current;
    const idChanged = prevIdClientRef.current !== idClient;

    if ((firstTime || idChanged) && isEmptyText && isEmptyEstat && isEmptyClient) {
      if (idClientInicial) {
        setEffClient(idClientInicial);
        setEffEstat("");
        setEffFilterText("");
      } else {
        setEffClient(null);
        setEffEstat("Esborrany");
        setEffFilterText("");
      }
    }

    didInitRef.current = true;
    prevIdClientRef.current = idClient;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idClient]);

  const [pressupostos, setPressupostosState] = useState([]);
  const [filteredPressupostos, setFilteredPressupostos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "idPresIntern", direction: "desc" });
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const selectedRowRef = useRef(null);

  useEffect(() => {
    const fetchPressupostos = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Pressupostos.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            idClient: effClient || null,
          }),
        });
        const result = await response.json();
        if (result.success) {
          setPressupostosState(result.pressupostos);
          setFilteredPressupostos(result.pressupostos);
        } else {
          setPressupostosState([]);
          setFilteredPressupostos([]);
        }
      } catch (err) {
        console.error("Error carregant pressupostos:", err);
        setPressupostosState([]);
        setFilteredPressupostos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPressupostos();
  }, [empresaSeleccionada, effClient, reloadToken]);

  useEffect(() => {
    const fetchClients = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Clientspressupostos.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        setClients(result.success ? result.clients : []);
      } catch (err) {
        console.error("Error carregant clients:", err);
        setClients([]);
      }
    };
    fetchClients();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const text = (effFilterText || "").toLowerCase();
    const filtered = pressupostos.filter((p) => {
      const matchesText =
        text === "" ||
        (p.NomClient || "").toLowerCase().includes(text) ||
        generateAddress(p).toLowerCase().includes(text) ||
        (p.Comentaris || "").toLowerCase().includes(text) ||
        (p.Poblacio || "").toLowerCase().includes(text);

      const matchesEstat =
        (effEstat || "").length === 0 || (p.EstatGlobal || "").includes(effEstat);

      const matchesClient = !effClient || p.Clients_idClients === effClient;

      return matchesText && matchesEstat && matchesClient;
    });
    setFilteredPressupostos(filtered);
    setPage(1);
  }, [effFilterText, effEstat, effClient, pressupostos]);

  useEffect(() => {
    if (!selectedPressupost) return;
    const stillVisible = filteredPressupostos.some(
      (p) => p.idPressupostos === selectedPressupost.idPressupostos
    );
    if (!stillVisible) onPressupostSelect?.(null);
  }, [filteredPressupostos, selectedPressupost, onPressupostSelect]);

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  const preuVentaNum = (p) => {
    if (p.Intermediari && Number(p.PreuFinalVentaInter) > 0) {
      return Number(p.PreuFinalVentaInter) || 0;
    }
    return Number(p.PreuFinalVenta) || 0;
  };

  const sortPressupostos = (rows, { key, direction }) => {
    if (!key) return rows;
    const dir = direction === "asc" ? 1 : -1;
    const nums = new Set(["VersioAcceptada"]);
    return [...rows].sort((a, b) => {
      if (key === "PreuFinalVenta") return (preuVentaNum(a) - preuVentaNum(b)) * dir;
      if (nums.has(key)) return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * dir;
      if (key === "idPresIntern") return collator.compare(a[key] ?? "", b[key] ?? "") * dir;
      const va = (a[key] ?? "").toString();
      const vb = (b[key] ?? "").toString();
      return va.localeCompare(vb, "ca", { numeric: true, sensitivity: "base" }) * dir;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const sortedPressupostos = sortPressupostos(filteredPressupostos, sortConfig);
  const total = sortedPressupostos.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = sortedPressupostos.slice(inici, inici + pageSize);

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const lastSigRef = useRef("");

  useEffect(() => {
    if (!setPressupostos) return;
    const sig = sortedPressupostos.map((x) => x.idPressupostos).join(",");
    if (sig !== lastSigRef.current) {
      setPressupostos(sortedPressupostos);
      lastSigRef.current = sig;
    }
  }, [sortedPressupostos, setPressupostos]);

  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedPressupost, sortedPressupostos]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, pressupost) => {
    e.stopPropagation();
    const id = pressupost.idPressupostos;
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = 280;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const pressupostMenu = menuObert
    ? sortedPressupostos.find((p) => p.idPressupostos === menuObert)
    : null;

  const tancarI = (fn) => {
    setMenuObert(null);
    fn?.();
  };

  const th = (key, label, extra = "") => (
    <th
      className={`${sortConfig.key === key ? "is-sorted" : ""} ${extra}`.trim()}
      onClick={() => handleSort(key)}
    >
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (pressupost) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === pressupost.idPressupostos ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, pressupost)}
      >
        …
      </button>
    </div>
  );

  const preuVenta = (p) => {
    if (p.Intermediari && p.PreuFinalVentaInter > 0) {
      return `${formatNumero(p.PreuFinalVentaInter)} € (${formatNumero(p.PreuFinalVenta)} €)`;
    }
    if (p.PreuFinalVenta) return `${formatNumero(p.PreuFinalVenta)} €`;
    return "—";
  };

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per client, adreça…"
            value={effFilterText}
            onChange={(e) => setEffFilterText((e.target.value || "").toLowerCase())}
          />
        </div>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Estat</span>
          <select className="dv2-select" value={effEstat} onChange={(e) => setEffEstat(e.target.value)}>
            <option value="">Tots</option>
            <option value="Esborrany">Esborrany</option>
            <option value="Presentat">Presentat</option>
            <option value="Acceptat">Acceptat</option>
            <option value="Rebutjat">Rebutjat</option>
          </select>
        </label>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Client</span>
          <select
            className="dv2-select"
            value={effClient || ""}
            onChange={(e) => setEffClient(Number(e.target.value) || null)}
          >
            <option value="">Tots els clients</option>
            {clients.map((c) => (
              <option key={c.idClients} value={c.idClients}>
                {c.NomClient}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant pressupostos…</p>
      ) : sortedPressupostos.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap pressupost.</p>
      ) : (
        <>
      {isNarrow ? (
        <div className="dv2-recordList" style={{ display: "flex" }}>
          {paginaItems.map((p) => {
            const isSelected = selectedPressupost?.idPressupostos === p.idPressupostos;
            return (
              <article
                key={p.idPressupostos}
                className={`dv2-record ${isSelected ? "is-selected" : ""}`}
                onClick={() => onPressupostSelect?.(p)}
                onDoubleClick={() => onConsultar?.(p)}
              >
                <div>
                  <p className="dv2-record-title">
                    {[p.idPresIntern, p.NomClient].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="dv2-record-meta">
                    {[
                      formatDate(p.DataAlta),
                      p.Comentaris,
                      p.Poblacio,
                      p.EstatGlobal,
                      preuVenta(p) !== "—" ? preuVenta(p) : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                </div>
                {teAccionsFila && renderAccions(p)}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            <thead>
              <tr>
                {th("idPresIntern", "Pressupost")}
                {th("DataAlta", "Data alta")}
                {th("NomClient", "Client")}
                {th("Comentaris", "Descripció")}
                {th("NomVia", "Adreça")}
                {th("Poblacio", "Població")}
                {th("PreuFinalVenta", "Preu venda", "dv2-num")}
                {th("EstatGlobal", "Estat")}
                {th("VersioAcceptada", "Versió acceptada")}
                {teAccionsFila && <th className="dv2-table-actions"></th>}
              </tr>
            </thead>
            <tbody>
              {paginaItems.map((p) => {
                const isSelected = selectedPressupost?.idPressupostos === p.idPressupostos;
                return (
                  <tr
                    key={p.idPressupostos}
                    ref={isSelected ? selectedRowRef : null}
                    className={isSelected ? "is-selected" : ""}
                    onClick={() => onPressupostSelect?.(p)}
                    onDoubleClick={() => onConsultar?.(p)}
                  >
                    <td>{p.idPresIntern}</td>
                    <td>{formatDate(p.DataAlta)}</td>
                    <td>{p.NomClient || "—"}</td>
                    <td className="dv2-cell-clip" title={p.Comentaris || ""}>
                      {p.Comentaris || "—"}
                    </td>
                    <td className="dv2-cell-clip" title={generateAddress(p) || ""}>
                      {generateAddress(p) || "—"}
                    </td>
                    <td>{p.Poblacio || "—"}</td>
                    <td className="dv2-num">{preuVenta(p)}</td>
                    <td>{p.EstatGlobal || "—"}</td>
                    <td>{p.VersioAcceptada || "—"}</td>
                    {teAccionsFila && <td className="dv2-table-actions">{renderAccions(p)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {teAccionsFila &&
        pressupostMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && onConsultar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => tancarI(() => onConsultar(pressupostMenu))}
              >
                Consultar
              </button>
            )}
            {pucModificar && onModificar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => tancarI(() => onModificar(pressupostMenu))}
              >
                Modificar
              </button>
            )}
            {pucModificar && onOfertes && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => tancarI(() => onOfertes(pressupostMenu))}
              >
                Ofertes
              </button>
            )}
            {pucVersions && onVersions && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => tancarI(() => onVersions(pressupostMenu))}
              >
                Versions
              </button>
            )}
            {pucEsborrar && onEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => tancarI(() => onEsborrar(pressupostMenu))}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SeleccionarPressupost;
